import { useEffect, useRef, useState } from 'react'
import { Link, useInRouterContext } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'
import {
  BellIcon,
  BellAlertIcon,
  UserPlusIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  AtSymbolIcon,
  ChartBarIcon,
  TrophyIcon,
  UserGroupIcon,
  DocumentTextIcon,
  FlagIcon,
  WrenchScrewdriverIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline'
import { adminAPI, communityAPI, priceAlertsAPI, signalsAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import ResultsDisclaimer from './ResultsDisclaimer'
import useEscapeKey from '../hooks/useEscapeKey'
import type { AlertTrigger, ClosedSignalEvent, Notification, NotificationType } from '../types'
import type { EngineEvent } from '../types/adminDiagnostics'
import { displaySymbol } from '../utils/displaySymbol'
import { STAND_DOWN_REASONS } from './dashboard/engineActivity'

const NOTIFICATION_ICONS: Record<NotificationType, typeof BellIcon> = {
  FOLLOW: UserPlusIcon,
  LIKE: HeartIcon,
  COMMENT: ChatBubbleLeftIcon,
  MENTION: AtSymbolIcon,
  SIGNAL_SHARED: ChartBarIcon,
  ACHIEVEMENT: TrophyIcon,
  COMMUNITY_INVITE: UserGroupIcon,
  COMMUNITY_POST: DocumentTextIcon,
  COURSE_UPDATE: BookOpenIcon,
  PRICE_ALERT: BellAlertIcon,
  SIGNAL_CLOSED: FlagIcon,
  ENGINE_EVENT: WrenchScrewdriverIcon,
}

const POLL_INTERVAL_MS = 30000

// The bell reads three sources: community notifications, fired price alerts
// from AlertTriggerHistory, and real signal closes -- plus, for superusers
// only, a fourth: engine fact alerts. Their ids come from different tables and
// can collide, so every entry carries its source, and that decides both the
// React key and how it is marked read.
type Source = 'community' | 'price_alert' | 'signal' | 'engine'

interface BellEntry {
  key: string
  source: Source
  id: number
  type: NotificationType
  title: string
  message: string
  is_read: boolean
  created_at: string
  /** Engine alerts open their event on the diagnostics page; a day of signal closes opens the Track Record. */
  href?: string
  /**
   * A folded row (engine events with the same trigger, or one day's signal
   * closes): every id in it, and the unread ones, which opening the row marks
   * read together.
   */
  eventIds?: number[]
  unreadEventIds?: number[]
  /** Engine only: "×3 since Oct 11, 09:00" for a folded run. */
  foldNote?: string
}

const usd = (value: number) =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Math.abs(value) < 1 ? 6 : 2,
  })

const ALERT_TITLES: Record<AlertTrigger['alert_type'], (t: AlertTrigger) => string> = {
  price_above: (t) => `${displaySymbol(t.symbol)} above ${usd(t.threshold)}`,
  price_below: (t) => `${displaySymbol(t.symbol)} below ${usd(t.threshold)}`,
  price_change_up: (t) => `${displaySymbol(t.symbol)} up ${t.threshold}%`,
  price_change_down: (t) => `${displaySymbol(t.symbol)} down ${t.threshold}%`,
  volume_spike: (t) => `${displaySymbol(t.symbol)} volume up ${t.threshold}%`,
  volatility_high: (t) => `${displaySymbol(t.symbol)} volatility above ${t.threshold}%`,
}

const fromCommunity = (n: Notification): BellEntry => ({
  key: `community:${n.id}`,
  source: 'community',
  id: n.id,
  type: n.type,
  title: n.title,
  message: n.message,
  is_read: n.is_read,
  created_at: n.created_at,
  ...(n.href ? { href: n.href } : {}),
})

const fromTrigger = (t: AlertTrigger): BellEntry => ({
  key: `price_alert:${t.id}`,
  source: 'price_alert',
  id: t.id,
  type: 'PRICE_ALERT',
  title: (ALERT_TITLES[t.alert_type] ?? ((x: AlertTrigger) => `${displaySymbol(x.symbol)} price alert`))(t),
  message: `Price alert fired at ${usd(t.trigger_price)}`,
  is_read: t.is_read,
  created_at: t.triggered_at,
})

// Signal closes are platform-wide, so the server has no per-user read state
// for them. This browser keeps it: a watermark (closes after seenAt are new)
// plus the ids read one at a time since. A first visit starts the watermark
// now rather than announcing the whole history as unread. Storage can be
// unavailable (private mode, blocked site data); then nothing counts as unread.
const SIGNAL_READ_KEY = 'fincreator.bell.signalCloses'
const MAX_READ_IDS = 200

interface SignalReadState {
  seenAt: string
  readIds: number[]
}

const saveSignalReadState = (state: SignalReadState) => {
  try {
    localStorage.setItem(SIGNAL_READ_KEY, JSON.stringify(state))
  } catch {
    // Read state is a convenience; the bell works without it.
  }
}

const loadSignalReadState = (): SignalReadState | null => {
  try {
    const raw = localStorage.getItem(SIGNAL_READ_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (typeof parsed?.seenAt === 'string' && !Number.isNaN(Date.parse(parsed.seenAt))) {
        return {
          seenAt: parsed.seenAt,
          readIds: Array.isArray(parsed.readIds) ? parsed.readIds.filter(Number.isInteger) : [],
        }
      }
    }
    const fresh = { seenAt: new Date().toISOString(), readIds: [] }
    localStorage.setItem(SIGNAL_READ_KEY, JSON.stringify(fresh))
    return fresh
  } catch {
    return null
  }
}

const isSignalUnread = (e: ClosedSignalEvent, state: SignalReadState | null) =>
  state !== null &&
  Date.parse(e.closed_at) > Date.parse(state.seenAt) &&
  !state.readIds.includes(e.id)

// The headline is the server's outcome: the record's gross verdict, the same
// one the Track Record and the Telegram close alert give. The percent move
// and R beside it are gross too, before fees, like every result a user sees;
// R never re-calls the verdict.
const OUTCOME_HEADLINES: Record<NonNullable<ClosedSignalEvent['outcome']>, string> = {
  win: 'WIN',
  loss: 'LOSS',
  breakeven: 'BREAK-EVEN',
}

const EXIT_REASONS: Record<NonNullable<ClosedSignalEvent['exit_reason']>, string> = {
  target: 'hit target',
  stop: 'stopped out',
  expired: 'expired',
}

const signedR = (value: number) =>
  `${value > 0 ? '+' : value < 0 ? '\u2212' : ''}${Math.abs(value).toFixed(2)}R`

const signedPct = (value: number) =>
  `${value > 0 ? '+' : value < 0 ? '\u2212' : ''}${Math.abs(value).toFixed(2)}%`

const fromClosedSignal = (e: ClosedSignalEvent, state: SignalReadState | null): BellEntry => {
  const direction = e.signal_type.toUpperCase()
  const title = e.outcome
    ? `${displaySymbol(e.symbol)} ${direction} closed \u2014 ${OUTCOME_HEADLINES[e.outcome]}`
    : `${displaySymbol(e.symbol)} ${direction} closed`
  const figures = [
    e.gross_pnl_pct !== null ? signedPct(e.gross_pnl_pct) : null,
    e.r_multiple !== null ? signedR(e.r_multiple) : null,
  ].filter((figure): figure is string => figure !== null)
  const parts = [figures.length ? `${figures.join(' · ')} before fees` : 'Result unmeasured']
  if (e.exit_reason && EXIT_REASONS[e.exit_reason]) parts.push(EXIT_REASONS[e.exit_reason])
  // Listed, but out of every record figure: say so beside the result.
  if (e.excluded_from_record) parts.push('excluded from the record')
  return {
    key: `signal:${e.id}`,
    source: 'signal',
    id: e.id,
    type: 'SIGNAL_CLOSED',
    title,
    message: parts.join(' \u00b7 '),
    is_read: !isSignalUnread(e, state),
    created_at: e.closed_at,
  }
}

// One row per local day of closes: the engine closes 10-20 signals on a busy
// day, and a row (and a badge count) for each buried everything else and held
// the bell at 99+. A day with a single close keeps its own row.
const dayOf = (iso: string) => format(new Date(iso), 'yyyy-MM-dd')

export const foldSignalCloses = (events: ClosedSignalEvent[], state: SignalReadState | null): BellEntry[] => {
  const days = new Map<string, ClosedSignalEvent[]>()
  for (const e of [...events].sort((a, b) => Date.parse(b.closed_at) - Date.parse(a.closed_at))) {
    const day = dayOf(e.closed_at)
    days.set(day, [...(days.get(day) ?? []), e])
  }
  return Array.from(days.values()).map((closes) => {
    if (closes.length === 1) return fromClosedSignal(closes[0], state)
    const newest = closes[0]
    const counted = closes.filter((e) => !e.excluded_from_record)
    const tally = (outcome: ClosedSignalEvent['outcome']) => counted.filter((e) => e.outcome === outcome).length
    const wins = tally('win')
    const losses = tally('loss')
    const breakevens = tally('breakeven')
    const outcomes = [
      `${wins} win${wins === 1 ? '' : 's'}`,
      `${losses} loss${losses === 1 ? '' : 'es'}`,
      ...(breakevens ? [`${breakevens} break-even`] : []),
    ]
    const rs = counted.map((e) => e.r_multiple).filter((r): r is number => r !== null)
    const each = closes
      // The coin alone: a day can list a dozen, all against USDT.
      .map((e) => `${displaySymbol(e.symbol).split('/')[0]} ${e.r_multiple !== null ? signedR(e.r_multiple) : '\u2014'}`)
      .join(', ')
    const excluded = closes.length - counted.length
    const parts = [
      ...(rs.length ? [`${signedR(rs.reduce((sum, r) => sum + r, 0))} before fees`] : []),
      each,
      ...(excluded ? [`${excluded} excluded from the record`] : []),
    ]
    const unread = closes.filter((e) => isSignalUnread(e, state)).map((e) => e.id)
    return {
      key: `signal-day:${dayOf(newest.closed_at)}`,
      source: 'signal' as const,
      id: newest.id,
      type: 'SIGNAL_CLOSED' as NotificationType,
      title: `${closes.length} signals closed \u2014 ${outcomes.join(', ')}`,
      message: parts.join(' \u00b7 '),
      is_read: unread.length === 0,
      created_at: newest.closed_at,
      href: '/track-record',
      eventIds: closes.map((e) => e.id),
      unreadEventIds: unread,
    }
  })
}

/** Unread signal rows as the bell shows them: days with an unread close. */
export const unreadSignalRows = (events: ClosedSignalEvent[], state: SignalReadState | null): number =>
  new Set(events.filter((e) => isSignalUnread(e, state)).map((e) => dayOf(e.closed_at))).size

// Engine alerts are deterministic facts (no AI), superusers only. Their read
// state is per admin on the server, so an alert that fired overnight is still
// unread here on any device.
const ENGINE_TITLES: Record<EngineEvent['severity'], string> = {
  critical: 'Engine alert',
  warning: 'Engine warning',
  info: 'Engine update',
}

// Rows stored before the stand-down message named its gates in words carry
// the raw reasons ("risk gate (portfolio_heat_cap)"). Say them as the
// dashboard does.
const RAW_REASONS = new RegExp(`\\b(${Object.keys(STAND_DOWN_REASONS).join('|')})\\b`, 'g')

export const engineSummaryText = (summary: string): string =>
  summary
    .replace(/risk gate \(([a-z_]+(?:, [a-z_]+)*)\)/g, (_m, reasons: string) =>
      `risk gate (${reasons
        .split(', ')
        .map((r) => (STAND_DOWN_REASONS[r] ? `the ${STAND_DOWN_REASONS[r]}` : r.replace(/_/g, ' ')))
        .join(' and ')})`
    )
    .replace(RAW_REASONS, (r) => `the ${STAND_DOWN_REASONS[r]}`)

/**
 * Engine events newest first, every event of the same trigger folded into one
 * row (not only back-to-back ones: a losing streak of 5, then other alerts,
 * then 7, is one condition): the newest one's text, "×N since <first time>".
 * Display only: every event keeps its own read state on the server.
 */
export const foldEngineEvents = (events: EngineEvent[]): BellEntry[] => {
  const sorted = [...events].sort((a, b) => Date.parse(b.fired_at) - Date.parse(a.fired_at))
  const byTrigger = new Map<string, EngineEvent[]>()
  for (const e of sorted) byTrigger.set(e.trigger, [...(byTrigger.get(e.trigger) ?? []), e])
  const runs = Array.from(byTrigger.values())
  return runs.map((run) => {
    const newest = run[0]
    const first = run[run.length - 1]
    const unread = run.filter((e) => !e.read).map((e) => e.id)
    return {
      key: `engine:${newest.id}`,
      source: 'engine',
      id: newest.id,
      type: 'ENGINE_EVENT',
      title: ENGINE_TITLES[newest.severity] ?? 'Engine alert',
      message: engineSummaryText(newest.summary),
      is_read: unread.length === 0,
      created_at: newest.fired_at,
      href: `/admin/diagnostics?event=${newest.id}`,
      eventIds: run.map((e) => e.id),
      unreadEventIds: unread,
      foldNote: run.length > 1 ? `×${run.length} since ${format(new Date(first.fired_at), 'MMM d, HH:mm')}` : undefined,
    }
  })
}

// Promise.allSettled only catches rejections. Starting each call inside a
// .then turns a synchronous throw into one too, so a broken source cannot
// take the other down with it.
const settle = <T,>(...calls: Array<() => Promise<T>>) =>
  Promise.allSettled(calls.map((call) => Promise.resolve().then(call)))

const newestFirst = (a: BellEntry, b: BellEntry) =>
  new Date(b.created_at).getTime() - new Date(a.created_at).getTime()

interface NotificationBellProps {
  /** Which side of the bell button the dropdown panel should open from. Defaults to 'right'. */
  panelAlign?: 'left' | 'right'
  /** Whether the panel opens below the bell or above it. A bell pinned to the
   *  bottom of the screen (the sidebar's user section) must open upward, or
   *  the panel lands off-screen. Defaults to 'below'. */
  panelPlacement?: 'below' | 'above'
}

const NotificationBell = ({ panelAlign = 'right', panelPlacement = 'below' }: NotificationBellProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [entries, setEntries] = useState<BellEntry[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { user } = useAuthStore()
  const isAdmin = Boolean(user?.is_superuser)
  const inRouter = useInRouterContext()

  // Each source is read independently: one failing must not blank the other.
  const loadUnreadCount = async () => {
    const state = loadSignalReadState()
    const [community, alerts, signals, engine] = await settle(
      () => communityAPI.getUnreadNotificationCount(),
      () => priceAlertsAPI.getUnreadTriggerCount(),
      async () =>
        state ? unreadSignalRows(await signalsAPI.getClosedFeed({ since: state.seenAt, limit: 100 }), state) : 0,
      async () => (isAdmin ? (await adminAPI.engineEvents({ unread: true, limit: 1 })).unread_count : 0)
    )
    const settled = [community, alerts, signals, engine].filter(
      (r): r is PromiseFulfilledResult<number> => r.status === 'fulfilled'
    )
    // All failed: leave the badge as it was rather than showing a false zero.
    if (settled.length === 0) return
    setUnreadCount(settled.reduce((sum, r) => sum + (r.value || 0), 0))
  }

  useEffect(() => {
    loadUnreadCount()
    const interval = setInterval(loadUnreadCount, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
    // isAdmin: an admin who signs in on a mounted layout gets their count now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useEscapeKey(() => {
    setIsOpen(false)
    buttonRef.current?.focus()
  }, isOpen)

  const toggleOpen = async () => {
    const next = !isOpen
    setIsOpen(next)
    if (!next) return

    setLoading(true)
    const state = loadSignalReadState()
    const [community, alerts, signals, engine] = await settle<
      Notification[] | AlertTrigger[] | ClosedSignalEvent[] | EngineEvent[]
    >(
      () => communityAPI.getNotifications(),
      () => priceAlertsAPI.getTriggers(),
      () => signalsAPI.getClosedFeed(),
      async () => (isAdmin ? (await adminAPI.engineEvents({ limit: 20 })).events : [])
    )
    const merged: BellEntry[] = []
    if (community.status === 'fulfilled') {
      merged.push(...((community.value || []) as Notification[]).map(fromCommunity))
    }
    if (alerts.status === 'fulfilled') {
      merged.push(...((alerts.value || []) as AlertTrigger[]).map(fromTrigger))
    }
    if (signals.status === 'fulfilled') {
      merged.push(...foldSignalCloses((signals.value || []) as ClosedSignalEvent[], state))
    }
    if (engine.status === 'fulfilled') {
      merged.push(...foldEngineEvents((engine.value || []) as EngineEvent[]))
    }
    if (
      community.status === 'rejected' &&
      alerts.status === 'rejected' &&
      signals.status === 'rejected'
    ) {
      const error: any = community.reason
      toast.error(error?.response?.data?.detail || 'Failed to load notifications')
    }
    setEntries(merged.sort(newestFirst))
    setLoading(false)
  }

  const handleEntryClick = async (entry: BellEntry) => {
    if (entry.is_read) return

    try {
      if (entry.source === 'signal') {
        const state = loadSignalReadState()
        if (state) {
          saveSignalReadState({
            ...state,
            readIds: [...state.readIds, ...(entry.unreadEventIds ?? [entry.id])].slice(-MAX_READ_IDS),
          })
        }
      } else if (entry.source === 'engine') {
        // A folded row marks every unread event in it.
        await Promise.all((entry.unreadEventIds ?? [entry.id]).map((id) => adminAPI.markEngineEventSeen(id, 'opened')))
      } else if (entry.source === 'price_alert') {
        await priceAlertsAPI.markTriggerAsRead(entry.id)
      } else {
        await communityAPI.markNotificationAsRead(entry.id)
      }
      setEntries((prev) => prev.map((e) => (e.key === entry.key ? { ...e, is_read: true, unreadEventIds: [] } : e)))
      setUnreadCount((prev) => Math.max(0, prev - (entry.source === 'engine' ? entry.unreadEventIds?.length ?? 1 : 1)))
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update notification')
    }
  }

  const handleMarkAllRead = async () => {
    const [community, alerts, engine] = await settle<unknown>(
      () => communityAPI.markAllNotificationsAsRead(),
      () => priceAlertsAPI.markAllTriggersAsRead(),
      async () => (isAdmin ? adminAPI.markAllEngineEventsSeen() : null)
    )
    const done = new Set<Source>()
    if (community.status === 'fulfilled') done.add('community')
    if (alerts.status === 'fulfilled') done.add('price_alert')
    if (engine.status === 'fulfilled') done.add('engine')

    // Signal closes are read locally: move the watermark to the newest close
    // shown (the server's clock, not this browser's), and drop the per-id
    // reads it now covers.
    const state = loadSignalReadState()
    if (state) {
      const newest = entries
        .filter((e) => e.source === 'signal')
        .reduce((max, e) => Math.max(max, Date.parse(e.created_at)), Date.parse(state.seenAt))
      saveSignalReadState({ seenAt: new Date(newest).toISOString(), readIds: [] })
    }
    done.add('signal')

    setEntries((prev) => prev.map((e) => (done.has(e.source) ? { ...e, is_read: true } : e)))
    if (done.size === 4) {
      setUnreadCount(0)
    } else {
      // Only part of it went through: recount rather than guess what is left.
      loadUnreadCount()
      const failed = [community, alerts, engine].find((r) => r.status === 'rejected') as
        | PromiseRejectedResult
        | undefined
      const error: any = failed?.reason
      toast.error(error?.response?.data?.detail || 'Failed to mark notifications as read')
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        onClick={toggleOpen}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-100"
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-[1rem] h-4 px-1 text-[10px] font-semibold leading-none text-white bg-red-500 rounded-full dark:bg-accent dark:text-terminal-bg">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Notifications"
          className={`absolute w-80 max-h-96 overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 z-50 dark:bg-terminal-surface dark:border-terminal-border dark:shadow-black/40 ${
            panelAlign === 'left' ? 'left-0' : 'right-0'
          } ${panelPlacement === 'above' ? 'bottom-full mb-2' : 'mt-2'}`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b dark:border-terminal-border">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                role="menuitem"
                className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-accent dark:hover:text-accent-hover"
              >
                Mark all as read
              </button>
            )}
          </div>

          {loading ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">No notifications yet</div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-terminal-border">
              {entries.map((entry) => {
                const Icon = NOTIFICATION_ICONS[entry.type] || BellIcon
                const itemClass = `w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-white/5 ${
                  entry.is_read ? '' : 'bg-primary-50 dark:bg-terminal-raised'
                }`
                const body = (
                  <>
                    <Icon className="h-5 w-5 text-primary-600 mt-0.5 shrink-0 dark:text-accent" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{entry.title}</p>
                      <p className="text-sm text-gray-600 line-clamp-2 dark:text-gray-400">{entry.message}</p>
                      {entry.foldNote && (
                        <p className="text-xs font-mono text-gray-500 dark:text-gray-400" data-testid="bell-fold-note">
                          {entry.foldNote}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                        {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    {!entry.is_read && (
                      <span
                        className="h-2 w-2 mt-1.5 bg-primary-600 rounded-full shrink-0 dark:bg-accent"
                        aria-label="Unread"
                      />
                    )}
                  </>
                )
                // An engine alert opens its event on the diagnostics page,
                // and opening it marks it read for this admin.
                if (entry.href) {
                  const open = () => {
                    handleEntryClick(entry)
                    setIsOpen(false)
                  }
                  return (
                    <li key={entry.key}>
                      {inRouter ? (
                        <Link
                          to={entry.href}
                          onClick={open}
                          role="menuitem"
                          data-source={entry.source}
                          className={itemClass}
                        >
                          {body}
                        </Link>
                      ) : (
                        <a
                          href={entry.href}
                          onClick={open}
                          role="menuitem"
                          data-source={entry.source}
                          className={itemClass}
                        >
                          {body}
                        </a>
                      )}
                    </li>
                  )
                }
                return (
                  <li key={entry.key}>
                    <button
                      onClick={() => handleEntryClick(entry)}
                      role="menuitem"
                      data-source={entry.source}
                      className={itemClass}
                    >
                      {body}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          <ResultsDisclaimer className="border-t border-gray-100 px-4 py-2 dark:border-white/10" />
        </div>
      )}
    </div>
  )
}

export default NotificationBell
